import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import * as commentsApi from '../../api/comments';
import type {
  Comment,
  CommentPage,
  CommentTargetType,
  ReactionKind,
} from '../../types/comments';
import { commentKeys } from './commentKeys';

function genOptimisticId(): string {
  return `01OPTIM${Math.random().toString(36).slice(2, 10).toUpperCase()}${Date.now().toString(36).toUpperCase()}`;
}

function patchPages(
  qc: ReturnType<typeof useQueryClient>,
  key: readonly unknown[],
  patch: (pages: CommentPage[]) => CommentPage[]
) {
  qc.setQueryData<InfiniteData<CommentPage>>(key, (prev) => {
    if (!prev) return prev;
    return { ...prev, pages: patch(prev.pages) };
  });
}

function mapPages(
  qc: ReturnType<typeof useQueryClient>,
  key: readonly unknown[],
  mapper: (c: Comment) => Comment
) {
  patchPages(qc, key, (pages) =>
    pages.map((p) => ({ ...p, comments: p.comments.map(mapper) }))
  );
}

// ─── Create ────────────────────────────────────────────────────────────

interface CreateInput {
  targetType: CommentTargetType;
  targetId: string;
  parentId?: string | null;
  body: string;
  author: { id: string; name: string; avatar_url: string | null; role: string | null };
}

export function useCreateComment() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateInput) => {
      return commentsApi.createComment({
        target_type: input.targetType,
        target_id: input.targetId,
        parent_id: input.parentId ?? undefined,
        body: input.body,
      });
    },
    onMutate: async (input) => {
      const optimisticId = genOptimisticId();
      const optimistic: Comment = {
        id: optimisticId,
        target_type: input.targetType,
        target_id: input.targetId,
        parent_id: input.parentId ?? null,
        body: input.body,
        edited_at: null,
        deleted_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        author: input.author,
        reply_count: 0,
        reactions: {
          like: { count: 0, reacted: false },
          dislike: { count: 0, reacted: false },
          emoji: [],
        },
        pending: true,
      };

      if (input.parentId) {
        // Reply — append to the parent's replies cache (if loaded) and
        // bump the parent's reply_count in the top-level list cache.
        patchPages(qc, commentKeys.replies(input.parentId), (pages) =>
          pages.length === 0
            ? [{ comments: [optimistic] }]
            : pages.map((p, i) =>
                i === pages.length - 1
                  ? { ...p, comments: [...p.comments, optimistic] }
                  : p
              )
        );
        mapPages(qc, commentKeys.list(input.targetType, input.targetId), (c) =>
          c.id === input.parentId ? { ...c, reply_count: c.reply_count + 1 } : c
        );
      } else {
        // Top-level — prepend.
        patchPages(qc, commentKeys.list(input.targetType, input.targetId), (pages) => {
          if (pages.length === 0) return [{ comments: [optimistic] }];
          return pages.map((p, i) =>
            i === 0 ? { ...p, comments: [optimistic, ...p.comments] } : p
          );
        });
      }
      return { optimisticId };
    },
    onError: (_err, input, ctx) => {
      const optimisticId = ctx?.optimisticId;
      if (!optimisticId) return;
      if (input.parentId) {
        patchPages(qc, commentKeys.replies(input.parentId), (pages) =>
          pages.map((p) => ({
            ...p,
            comments: p.comments.filter((c) => c.id !== optimisticId),
          }))
        );
        mapPages(qc, commentKeys.list(input.targetType, input.targetId), (c) =>
          c.id === input.parentId
            ? { ...c, reply_count: Math.max(0, c.reply_count - 1) }
            : c
        );
      } else {
        patchPages(qc, commentKeys.list(input.targetType, input.targetId), (pages) =>
          pages.map((p) => ({
            ...p,
            comments: p.comments.filter((c) => c.id !== optimisticId),
          }))
        );
      }
    },
    onSuccess: (real, input, ctx) => {
      const optimisticId = ctx?.optimisticId;
      if (!optimisticId) return;
      // Swap optimistic row → hydrated row.
      if (input.parentId) {
        patchPages(qc, commentKeys.replies(input.parentId), (pages) =>
          pages.map((p) => ({
            ...p,
            comments: p.comments.map((c) => (c.id === optimisticId ? real : c)),
          }))
        );
      } else {
        patchPages(qc, commentKeys.list(input.targetType, input.targetId), (pages) =>
          pages.map((p) => ({
            ...p,
            comments: p.comments.map((c) => (c.id === optimisticId ? real : c)),
          }))
        );
      }
    },
  });
}

// ─── Edit ──────────────────────────────────────────────────────────────

interface EditInput {
  comment: Comment;
  body: string;
}

export function useEditComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EditInput) =>
      commentsApi.editComment(input.comment.id, { body: input.body }),
    onMutate: async ({ comment, body }) => {
      const previous = comment;
      const patched: Comment = {
        ...comment,
        body,
        edited_at: new Date().toISOString(),
      };
      // Patch wherever this id lives.
      mapPages(qc, commentKeys.list(comment.target_type, comment.target_id), (c) =>
        c.id === comment.id ? patched : c
      );
      if (comment.parent_id) {
        mapPages(qc, commentKeys.replies(comment.parent_id), (c) =>
          c.id === comment.id ? patched : c
        );
      }
      return { previous };
    },
    onError: (_err, { comment }, ctx) => {
      if (!ctx?.previous) return;
      mapPages(qc, commentKeys.list(comment.target_type, comment.target_id), (c) =>
        c.id === comment.id ? ctx.previous : c
      );
      if (comment.parent_id) {
        mapPages(qc, commentKeys.replies(comment.parent_id), (c) =>
          c.id === comment.id ? ctx.previous : c
        );
      }
    },
    onSuccess: (real, { comment }) => {
      mapPages(qc, commentKeys.list(comment.target_type, comment.target_id), (c) =>
        c.id === real.id ? real : c
      );
      if (comment.parent_id) {
        mapPages(qc, commentKeys.replies(comment.parent_id), (c) =>
          c.id === real.id ? real : c
        );
      }
    },
  });
}

// ─── Delete (soft) ─────────────────────────────────────────────────────

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (comment: Comment) => {
      await commentsApi.deleteComment(comment.id);
      return comment;
    },
    onMutate: async (comment) => {
      const previous = comment;
      const tomb: Comment = {
        ...comment,
        body: '[deleted]',
        deleted_at: new Date().toISOString(),
      };
      mapPages(qc, commentKeys.list(comment.target_type, comment.target_id), (c) =>
        c.id === comment.id ? tomb : c
      );
      if (comment.parent_id) {
        mapPages(qc, commentKeys.replies(comment.parent_id), (c) =>
          c.id === comment.id ? tomb : c
        );
      }
      return { previous };
    },
    onError: (_err, comment, ctx) => {
      if (!ctx?.previous) return;
      mapPages(qc, commentKeys.list(comment.target_type, comment.target_id), (c) =>
        c.id === comment.id ? ctx.previous : c
      );
      if (comment.parent_id) {
        mapPages(qc, commentKeys.replies(comment.parent_id), (c) =>
          c.id === comment.id ? ctx.previous : c
        );
      }
    },
  });
}

// ─── Reactions ─────────────────────────────────────────────────────────

interface ToggleReactionInput {
  comment: Comment;
  kind: ReactionKind;
  emoji?: string;
}

export function useToggleReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ comment, kind, emoji }: ToggleReactionInput) => {
      const reacted =
        kind === 'like'
          ? comment.reactions.like.reacted
          : kind === 'dislike'
            ? comment.reactions.dislike.reacted
            : comment.reactions.emoji.find((e) => e.emoji === emoji)?.reacted ?? false;
      if (reacted) {
        return commentsApi.removeReaction(comment.id, { kind, emoji });
      }
      return commentsApi.addReaction(comment.id, { kind, emoji });
    },
    onMutate: async ({ comment, kind, emoji }) => {
      const previous = comment;
      const patched = applyOptimisticReaction(comment, kind, emoji);
      mapPages(qc, commentKeys.list(comment.target_type, comment.target_id), (c) =>
        c.id === comment.id ? patched : c
      );
      if (comment.parent_id) {
        mapPages(qc, commentKeys.replies(comment.parent_id), (c) =>
          c.id === comment.id ? patched : c
        );
      }
      return { previous };
    },
    onError: (_err, { comment }, ctx) => {
      if (!ctx?.previous) return;
      mapPages(qc, commentKeys.list(comment.target_type, comment.target_id), (c) =>
        c.id === comment.id ? ctx.previous : c
      );
      if (comment.parent_id) {
        mapPages(qc, commentKeys.replies(comment.parent_id), (c) =>
          c.id === comment.id ? ctx.previous : c
        );
      }
    },
    onSuccess: (real, { comment }) => {
      mapPages(qc, commentKeys.list(comment.target_type, comment.target_id), (c) =>
        c.id === real.id ? real : c
      );
      if (comment.parent_id) {
        mapPages(qc, commentKeys.replies(comment.parent_id), (c) =>
          c.id === real.id ? real : c
        );
      }
    },
  });
}

function applyOptimisticReaction(
  comment: Comment,
  kind: ReactionKind,
  emoji?: string
): Comment {
  const reactions = { ...comment.reactions };
  if (kind === 'like') {
    const wasReacted = reactions.like.reacted;
    reactions.like = {
      reacted: !wasReacted,
      count: wasReacted
        ? Math.max(0, reactions.like.count - 1)
        : reactions.like.count + 1,
    };
    // If the user is liking and they had disliked, the server flips dislike too.
    if (!wasReacted && reactions.dislike.reacted) {
      reactions.dislike = {
        reacted: false,
        count: Math.max(0, reactions.dislike.count - 1),
      };
    }
  } else if (kind === 'dislike') {
    const wasReacted = reactions.dislike.reacted;
    reactions.dislike = {
      reacted: !wasReacted,
      count: wasReacted
        ? Math.max(0, reactions.dislike.count - 1)
        : reactions.dislike.count + 1,
    };
    if (!wasReacted && reactions.like.reacted) {
      reactions.like = {
        reacted: false,
        count: Math.max(0, reactions.like.count - 1),
      };
    }
  } else if (kind === 'emoji' && emoji) {
    const existing = reactions.emoji.find((e) => e.emoji === emoji);
    if (existing) {
      const wasReacted = existing.reacted;
      reactions.emoji = reactions.emoji
        .map((e) =>
          e.emoji === emoji
            ? {
                ...e,
                reacted: !wasReacted,
                count: wasReacted ? Math.max(0, e.count - 1) : e.count + 1,
              }
            : e
        )
        .filter((e) => e.count > 0 || e.reacted);
    } else {
      reactions.emoji = [
        ...reactions.emoji,
        { emoji, count: 1, reacted: true },
      ];
    }
  }
  return { ...comment, reactions };
}
